import {
  ApiAttributionModel,
  ApiChartTemplate,
  ApiChartType,
  ApiMetric,
} from "@incendium/api";
import { Box, Grid, Stack } from "@mui/material";
import AnalyticsCard from "features/analytics/components/AnalyticsCard";
import {
  metricByAttribution,
  metricsByAttributionModelType,
  orderAttributionMetrics,
  orderAttributionType,
} from "features/analytics";
import { AttributionMetric } from "features/analytics/types/types";
import ExplorerSidebar from "features/explorer/components/ExplorerSidebar";
import ExplorerTable from "features/explorer/components/ExplorerTable";
import { useExplorerContext } from "features/explorer/providers/ExplorerProvider";
import { useMemo, useState } from "react";
import { AttributionPerformanceExplorers } from "Pages/Analyse/QuickInsights/AttributionExplorerPage";
import { useMount } from "react-use";
import { useHasSalesLastYear } from "features/analytics/hooks/useHasHadSales";
import Loading from "Components/Loading/Loading";
import ExplorerTopBar from "./ExplorerTopBar";

function AttributionExplorer() {
  const [open, setOpen] = useState(false);
  const {
    selectedAttributionTypes,
    dimension,
    attributes,
    type,
    setSelectedAttributionTypes,
  } = useExplorerContext<AttributionPerformanceExplorers>();
  const { state, fetched } = useHasSalesLastYear();

  useMount(() => {
    if (selectedAttributionTypes.length === 0) {
      setSelectedAttributionTypes([
        ApiAttributionModel.EBA,
        ApiAttributionModel.LAST,
      ]);
    }
  });

  const metrics = useMemo(() => {
    if (!fetched) return [];
    let attMetrics: ApiMetric[] = [];
    if (state.hasSalesLastYear) {
      attMetrics = selectedAttributionTypes
        .map(metricsByAttributionModelType)
        .flat();
    } else {
      attMetrics = selectedAttributionTypes.map(
        (m) => metricByAttribution(m, AttributionMetric.MACRO) as ApiMetric,
      );
    }

    return [
      ApiMetric.METRIC_SESSIONS_COUNT,
      ...orderAttributionMetrics(attMetrics),
    ];
  }, [selectedAttributionTypes, type, fetched, state.hasSalesLastYear]);

  if (!fetched) return <Loading fullHeight />;
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        flexWrap: "nowrap",
        gap: 2,
      }}
    >
      <ExplorerTopBar onOpenSidebar={() => setOpen(true)} />

      <Box sx={{ minHeight: 0, flexGrow: 1 }} pb={2}>
        <Stack sx={{ height: "100%" }}>
          {state.hasSalesLastYear && (
            <Grid
              container
              spacing={2}
              wrap="nowrap"
              sx={{ height: 400, overflowX: "auto", overflowY: "hidden" }}
            >
              {orderAttributionType(selectedAttributionTypes).map((model) => (
                <Grid
                  item
                  xs={selectedAttributionTypes.length > 1 ? 6 : 12}
                  key={model + selectedAttributionTypes.length}
                  sx={{ minWidth: "50%" }}
                >
                  <AnalyticsCard
                    noborder={selectedAttributionTypes.length === 1}
                    square={selectedAttributionTypes.length === 1}
                    chart={{
                      name: `${model}`,
                      dimension: [dimension],
                      template: ApiChartTemplate.SNAPSHOT,
                      type: ApiChartType.BUBBLE,
                      attributes,
                      includePublisherReadyOnly: true,
                      yAxisKeys: [
                        {
                          key: "l",
                          fields: [
                            metricByAttribution(
                              model,
                              AttributionMetric.SALES,
                            ) as ApiMetric,
                            metricByAttribution(
                              model,
                              AttributionMetric.ROI,
                            ) as ApiMetric,
                            metricByAttribution(
                              model,
                              AttributionMetric.REVENUE,
                            ) as ApiMetric,
                          ],
                        },
                      ],
                      displayOptions: {
                        noTitle: true,
                        noFilters: true,
                        noAttributionToggle: true,
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          )}
          <Box mt={2} sx={{ flex: 1 }}>
            <ExplorerTable metrics={metrics} dontIncludeEmpty />
          </Box>
        </Stack>
      </Box>
      <ExplorerSidebar open={open} onClose={() => setOpen(false)} />
    </Box>
  );
}

export default AttributionExplorer;
